import {z} from 'zod/v4';

import {Condition} from '../types/condition';
import {ArrayFilter, ConditionFilter, DateFilter, PropertyFilter, ValueFilter} from '../types/filter';
import {ConditionSchema} from './condition';
import {ReferenceQuerySchema, ReferenceSchema} from './reference';
import {TemplateValueSchema} from './template';

const ArrayEqualityFilterSchema = z.object({
  field: z.string(),
  value: z.lazy(() => z.union([z.string(), ReferenceSchema])),
});

const InFilterSchema = z.object({
  value: z.lazy(() => z.union([ReferenceSchema, ReferenceQuerySchema, z.array(z.string())])),
});

const ArrayInFilterSchema = z.object({
  field: z.string(),
  value: z.lazy(() => z.array(z.string())),
});

export const ValueFilterSchema: z.ZodType<ValueFilter> = z.union([
  z.object({operator: z.literal('in')}).and(InFilterSchema),
  z.object({operator: z.literal('not in')}).and(InFilterSchema),
]);

export const ArrayFilterSchema: z.ZodType<ArrayFilter> = z.union([
  z.object({operator: z.literal('=')}).and(ArrayEqualityFilterSchema),
  z.object({operator: z.literal('!=')}).and(ArrayEqualityFilterSchema),
  z.object({operator: z.literal('in')}).and(ArrayInFilterSchema),
  z.object({operator: z.literal('not in')}).and(ArrayInFilterSchema),
]);

export const PropertyFilterSchema: z.ZodType<PropertyFilter> = z.union([
  z.object({
    operator: z.literal('has'),
    property: z.union([z.string(), TemplateValueSchema]),
    field: z.lazy(() => z.union([z.string(), ReferenceSchema])),
  }),
  z.object({
    operator: z.literal('not has'),
    property: z.union([z.string(), TemplateValueSchema]),
    field: z.lazy(() => z.union([z.string(), ReferenceSchema])),
  }),
  z.object({
    operator: z.literal('is'),
    property: z.union([z.string(), TemplateValueSchema]),
    value: z.lazy(() => z.union([z.string(), ReferenceSchema])),
  }),
  z.object({
    operator: z.literal('is not'),
    property: z.union([z.string(), TemplateValueSchema]),
    value: z.lazy(() => z.union([z.string(), ReferenceSchema])),
  }),
]);

const LogicalConditionFilterFields = {
  conditions: z.lazy((): z.ZodArray<z.ZodType<Condition>> => z.array(ConditionSchema)),
};

export const ConditionFilterSchema: z.ZodType<ConditionFilter> = z.discriminatedUnion('operator', [
  z.strictObject({operator: z.literal('and'), ...LogicalConditionFilterFields}),
  z.strictObject({operator: z.literal('or'), ...LogicalConditionFilterFields}),
  z.strictObject({operator: z.literal('not'), ...LogicalConditionFilterFields}),
]);

export const DateFilterSchema: z.ZodType<DateFilter> = z.object({
  operator: z.literal('date'),
  property: z.string(),
  value: z.object({
    min: z.string().optional(),
    max: z.string().optional(),
  }),
});
